import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const VERTICALS = [
  { v: "law_enforcement", label: "Law Enforcement" },
  { v: "fire_service", label: "Fire Service" },
  { v: "ems", label: "EMS" },
  { v: "dispatch_psap", label: "Dispatch / PSAP" },
  { v: "corrections", label: "Corrections" },
  { v: "school_safety", label: "School Safety" },
  { v: "military", label: "Military" },
  { v: "emergency_medicine", label: "Emergency Medicine" },
];

export default function RoleSelect() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("service_member");
  const [vertical, setVertical] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [inServiceDate, setInServiceDate] = useState("");
  const [agencies, setAgencies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) nav("/");
    if (user?.role) {
      // Already set
      nav(user.role === "agency_admin" ? "/agency" : "/app");
    }
  }, [user, nav]);

  useEffect(() => {
    if (vertical) {
      api.get(`/auth/agencies?vertical=${vertical}`).then((r) => setAgencies(r.data)).catch(() => {});
    }
  }, [vertical]);

  const submit = async (e) => {
    e.preventDefault();
    if (!vertical) { toast.error("Choose a vertical."); return; }
    if (role === "service_member" && !inServiceDate) {
      toast.error("In-service date is required."); return;
    }
    setSaving(true);
    try {
      const body = { role, vertical };
      if (role === "agency_admin") {
        if (!agencyName) { setSaving(false); toast.error("Provide agency name."); return; }
        body.agency_name = agencyName;
      } else {
        if (agencyId) body.agency_id = agencyId;
        body.in_service_date = new Date(inServiceDate).toISOString();
      }
      const r = await api.post("/auth/role", body);
      setUser(r.data);
      if (role === "service_member") {
        nav("/disclosure", { replace: true });
      } else {
        nav("/agency", { replace: true });
      }
    } catch (e) {
      toast.error("Could not save role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="mb-8 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Set up your account
        </div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Tell us how you'll use Haruki Health.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Choose your role and occupational vertical. You can't change this later — it shapes scenario delivery and confidentiality boundaries.
        </p>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Account setup</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6">
              <div className="space-y-3">
                <Label>I am a…</Label>
                <RadioGroup value={role} onValueChange={setRole} data-testid="role-radio-group">
                  <div className="flex items-center gap-3 rounded-md border border-border p-3">
                    <RadioGroupItem value="service_member" id="r1" data-testid="role-service-member" />
                    <Label htmlFor="r1" className="cursor-pointer">Service member (24/7 mobile training)</Label>
                  </div>
                  <div className="flex items-center gap-3 rounded-md border border-border p-3">
                    <RadioGroupItem value="agency_admin" id="r2" data-testid="role-agency-admin" />
                    <Label htmlFor="r2" className="cursor-pointer">Agency administrator (web dashboard)</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Occupational vertical</Label>
                <Select value={vertical} onValueChange={setVertical}>
                  <SelectTrigger data-testid="vertical-select"><SelectValue placeholder="Choose vertical" /></SelectTrigger>
                  <SelectContent>
                    {VERTICALS.map(v => (
                      <SelectItem key={v.v} value={v.v} data-testid={`vertical-option-${v.v}`}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {role === "service_member" ? (
                <>
                  <div className="space-y-2">
                    <Label>In-service date</Label>
                    <Input
                      data-testid="in-service-date-input"
                      type="date"
                      value={inServiceDate}
                      onChange={(e) => setInServiceDate(e.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground">Used to schedule annual refresher 30 days before anniversary.</p>
                  </div>

                  {agencies.length > 0 && (
                    <div className="space-y-2">
                      <Label>Agency (optional)</Label>
                      <Select value={agencyId} onValueChange={setAgencyId}>
                        <SelectTrigger data-testid="agency-select"><SelectValue placeholder="Choose an agency" /></SelectTrigger>
                        <SelectContent>
                          {agencies.map(a => (
                            <SelectItem key={a.agency_id} value={a.agency_id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <Label>Agency name</Label>
                  <Input
                    data-testid="agency-name-input"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    placeholder="e.g. Portland Police Bureau"
                    required
                  />
                </div>
              )}

              <Button
                data-testid="role-submit-button"
                type="submit"
                className="w-full"
                disabled={saving}
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              >
                {saving ? "Saving…" : "Continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
